/**
 * v25 BOLT-140 — the SELECTOR: mode means a size, not a road tier.
 *
 * THE PROBLEM WITH MODE-AS-TIER. Until now "bike" meant "secondary roads and
 * bigger are walls". That is a statement about McKinney, not about biking: the
 * same OpenMapTiles tier is a six-lane arterial in Texas, a shopping street in
 * Amsterdam and an avenue in Manhattan. Measured on real tiles the identical
 * setting produces 0.976 sq mi at Craig Ranch and 0.023 in the Jordaan. The
 * mode picker could not mean anything consistent, because it was not describing
 * the player, it was describing the road classification.
 *
 * THE INVERSION. Mode sets a TARGET SIZE - the ground a hider can actually
 * reach in their headstart. The barrier set stops being the answer and becomes
 * the SEARCH STRATEGY for hitting that target: try the walls this mode must
 * respect, and if the block they leave is far too big, add finer walls back
 * until it is not. The core algorithm is untouched and still completely
 * general: location plus a constraint set in, enclosed coordinates out.
 *
 * THREE RULES, in the order they apply:
 *
 *  1. SAFETY IS LEXICOGRAPHIC, NOT BLENDED. Each mode has a floor of lines that
 *     may never be crossed, and every candidate on the ladder is a SUPERSET of
 *     that floor. Adding walls can only shrink a block, so no rung can ever put
 *     the player across a road the mode says they must not cross. Size chooses
 *     among what remains and never buys its way past this. Jake: default foot,
 *     respect tertiary, accept a smaller map.
 *  2. PREFER OVERSHOOT, BUT BOUND IT. Too big is easy on the hider, who is
 *     headstart-capped anyway, but it costs the seeker long empty walks. So
 *     take the biggest candidate at or under `overshoot x target`, and only
 *     fall back to the smallest available if every candidate is over.
 *  3. RANK BY EDGE SAFETY TOO. Players concentrate on the perimeter - hiders
 *     hug it, seekers sweep it. A motorway edge and a river edge stop you
 *     equally, and only one of them is safe to walk beside for twenty minutes.
 *     Where two candidates both fit the target, the safer border wins.
 *
 * Pure TypeScript, no turf, no DOM: runs in the browser and in Node, like the
 * arrangement it drives.
 */
import { enclosingBlock, type BlockResult, type FaceFailure, type PlanarLine, type PlanarPoint } from './planarFace';
import { BARRIER_KINDS, searchWindowsFor, type BarrierKind, type TravelMode } from '../travelMode';
import { attributeRing } from './ringAttribution';

/* ------------------------------------------------------------------ *
 * 1. the target: headstart x travel speed
 * ------------------------------------------------------------------ */

/**
 * Every number that shapes a target, in ONE object.
 *
 * Deliberately parameters and not constants: Jake wants to "easily learn and
 * evolve these", and a tuning value buried in a function body is a value nobody
 * ever tunes. `boundary-lab.ts --targets` prints the whole table.
 */
export interface TargetModel {
  /** Straight-line travel speed while getting away, metres per second. */
  speedMps: Record<TravelMode, number>;
  /**
   * How much of the headstart turns into DISTANCE FROM THE START.
   *
   * Never 1. Three things eat it and all three are real: streets do not run
   * where you want to go (a road network delivers roughly 0.7 of the crow-flies
   * distance), a hider spends the back half of their headstart choosing and
   * settling into a spot rather than travelling, and nobody sprints for fifteen
   * minutes. 0.26 is the calibration that puts the three modes where the field
   * says they belong; it is one number, and it is the one to turn.
   */
  reachFraction: number;
  /** Accept up to this multiple of the target. Above it, add walls. */
  overshoot: number;
  /** Never target less than this, whatever the settings say. Square metres. */
  minTargetSqM: number;
}

export const DEFAULT_TARGET_MODEL: TargetModel = {
  // Brisk walk, an unhurried cyclist, and a car on suburban streets with
  // junctions and lights, not a car on a motorway.
  speedMps: { foot: 1.4, bike: 4.17, car: 7.65 },
  reachFraction: 0.26,
  // v26 BOLT-149 (Jake): 1.5 -> 4. The bound is a HARD FILTER with no
  // undershoot floor, so car used to reject a 2.47x overshoot and take a 0.25x
  // undershoot instead - landing on the identical block bike had already taken,
  // which is exactly what the field reported. Measured across the nine
  // acceptance locations: only craig car (0.976 -> 9.612) and ames car
  // (6.474 -> 8.922) move, monotonicity holds with zero violations, and the
  // other 25 cases are byte-identical.
  overshoot: 4,
  minTargetSqM: 60_000,
};

/** How far a hider actually gets from the start, in metres. */
export function reachRadiusM(mode: TravelMode, headStartMs: number, model: TargetModel = DEFAULT_TARGET_MODEL): number {
  return model.speedMps[mode] * (headStartMs / 1000) * model.reachFraction;
}

/**
 * The area worth searching: the disc a hider can reach in their headstart.
 *
 * This is the whole reason the target is not a hard-coded square-mile figure.
 * It moves with the settings the players actually chose, and it is the same
 * number Phase 2 will phrase as "about a ten minute walk across", because that
 * is literally what it is.
 */
export function targetAreaSqM(mode: TravelMode, headStartMs: number, model: TargetModel = DEFAULT_TARGET_MODEL): number {
  const r = reachRadiusM(mode, headStartMs, model);
  return Math.max(model.minTargetSqM, Math.PI * r * r);
}

/* ------------------------------------------------------------------ *
 * 2. safety: the floor, and the ladder above it
 * ------------------------------------------------------------------ */

/**
 * The lines a mode may NEVER put inside the play area.
 *
 * This is the safety contract, and it is the same set the v23 presets already
 * used - which is the happy part: today's behaviour is rung 0 of the new
 * ladder, so nothing gets less safe, some maps just get less silly.
 */
export const SAFETY_FLOOR: Record<TravelMode, readonly BarrierKind[]> = {
  // On foot every tier above residential is a road you should not be crossing
  // mid-block, and water and rail are walls for everyone.
  foot: ['tertiary', 'secondary', 'primary', 'water', 'rail'],
  // A tertiary road is a normal part of a bike route.
  bike: ['secondary', 'primary', 'water', 'rail'],
  // A car crosses anything short of a motorway, and `primary` is the umbrella
  // for trunk and motorway, which have no at-grade crossing to use.
  car: ['primary', 'water', 'rail'],
};

/** Finer tiers, in the order the ladder adds them back to shrink a block. */
const SHRINK_ORDER: readonly BarrierKind[] = ['secondary', 'tertiary'];

/**
 * The candidate ladder for a mode: the safety floor, then the floor plus each
 * finer tier in turn. Every rung is a superset of the floor, so rule 1 holds by
 * CONSTRUCTION rather than by a check that could be forgotten.
 *
 * Rungs are ordered biggest-block first (fewest walls first).
 */
export function candidateSets(mode: TravelMode): BarrierKind[][] {
  const floor = SAFETY_FLOOR[mode];
  const out: BarrierKind[][] = [[...floor]];
  const extra: BarrierKind[] = [];
  for (const k of SHRINK_ORDER) {
    if (floor.includes(k)) continue;
    extra.push(k);
    // Keep BARRIER_KINDS order so the set reads the same as the checklist.
    out.push(BARRIER_KINDS.filter((b) => floor.includes(b) || extra.includes(b)));
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * 3. edge safety: what the perimeter is made of
 * ------------------------------------------------------------------ */

/**
 * How pleasant each kind of wall is to spend twenty minutes beside, 0..1.
 *
 * A motorway and a river both stop you dead, so the geometry cannot tell them
 * apart - but one of them has a hard shoulder and lorries. Water and rail score
 * well because a player walks the bank or the fence line, not the thing itself.
 */
export const EDGE_SAFETY: Record<BarrierKind, number> = {
  water: 1,
  rail: 0.8,
  tertiary: 0.7,
  secondary: 0.45,
  primary: 0.15,
};

/**
 * A barrier line that remembers which kind of wall it is.
 *
 * The coordinates are MUTABLE here, unlike `PlanarLine`, purely so a harvest
 * can hand the same arrays straight to turf without a copy.
 *
 * v25 Phase 2: `name` rides along OPTIONALLY, for the composition label. It is
 * never used by the geometry or the ranking - only to tell the player that the
 * wall they are looking at is called Custer Rd.
 */
export interface ClassifiedBarrier extends PlanarLine {
  kind: BarrierKind;
  name?: string;
  readonly coordinates: number[][];
}

/**
 * Length-weighted mean edge safety of a ring, 0..1.
 *
 * Each sample along the perimeter is attributed to the NEAREST classified
 * barrier, which is the same trick the acceptance instrument uses and for the
 * same reason: the ring's vertices are noded, so they carry no provenance and
 * asking the algorithm "which line is this edge?" would be asking it to mark
 * its own homework.
 *
 * v25 Phase 2 moved the sampling itself into `ringAttribution.ts` unchanged, so
 * the composition label (which asks the same question about NAMES) cannot end
 * up describing a different perimeter than the one ranked here.
 */
export function edgeSafety(
  ring: readonly (readonly number[])[],
  barriers: readonly ClassifiedBarrier[],
  center: PlanarPoint,
  stepM = 20,
): { score: number; mix: Partial<Record<BarrierKind, number>> } {
  const { totalM, metresByLine } = attributeRing(ring, barriers, center, { stepM });
  let score = 0;
  const mix: Partial<Record<BarrierKind, number>> = {};
  for (const [li, metres] of metresByLine) {
    const k = barriers[li].kind;
    score += metres * EDGE_SAFETY[k];
    mix[k] = (mix[k] ?? 0) + metres;
  }
  for (const k of Object.keys(mix) as BarrierKind[]) mix[k] = (mix[k] ?? 0) / (totalM || 1);
  return { score: totalM ? score / totalM : 0, mix };
}

/* ------------------------------------------------------------------ *
 * 4. the selector
 * ------------------------------------------------------------------ */

export interface Candidate {
  barriers: BarrierKind[];
  /** null when this rung produced no enclosed block at all. */
  block: BlockResult | null;
  reason?: FaceFailure;
  areaSqM: number;
  /** area / target. 1 is exact, 2 is twice as big as it should be. */
  ratio: number;
  withinBound: boolean;
  edgeSafety: number;
  edgeMix: Partial<Record<BarrierKind, number>>;
  score: number;
  ms: number;
}

export interface SelectorOptions {
  headStartMs: number;
  model?: TargetModel;
  /** Windows to search. Defaults to the mode's schedule. */
  radii?: readonly number[];
  /** How much the edge-safety term can move the ranking, 0..1. Size stays the
   *  dominant term; this breaks ties and near-ties, it does not overrule. */
  edgeWeight?: number;
  /** Stop trying rungs once the wall clock passes this. Milliseconds. */
  budgetMs?: number;
  trace?: (line: string) => void;
}

export type SelectorOutcome =
  | { ok: true; chosen: Candidate; targetSqM: number; candidates: Candidate[]; ms: number; sizeCompromised: boolean }
  | { ok: false; reason: FaceFailure; targetSqM: number; candidates: Candidate[]; ms: number };

const DEFAULT_EDGE_WEIGHT = 0.25;

/**
 * Pick the play area for `mode` at `center` out of the classified barriers.
 *
 * `barriers` must carry every kind the ladder might ask for (the harvest is the
 * only place that knows a road's tier), and the selector filters per rung. The
 * geometry itself is `enclosingBlock`, called once per rung and not modified in
 * any way - which is what keeps this a policy layer rather than a second
 * algorithm to keep in step.
 */
export function selectBoundary(
  barriers: readonly ClassifiedBarrier[],
  center: PlanarPoint,
  mode: TravelMode,
  opts: SelectorOptions,
): SelectorOutcome {
  const t0 = Date.now();
  const model = opts.model ?? DEFAULT_TARGET_MODEL;
  const target = targetAreaSqM(mode, opts.headStartMs, model);
  const edgeWeight = opts.edgeWeight ?? DEFAULT_EDGE_WEIGHT;
  const radii = opts.radii ?? searchWindowsFor(mode);
  const sets = candidateSets(mode);
  const candidates: Candidate[] = [];
  let lastReason: FaceFailure = 'no-barriers';

  for (const set of sets) {
    if (opts.budgetMs !== undefined && Date.now() - t0 > opts.budgetMs && candidates.some((c) => c.block)) {
      opts.trace?.(`budget ${opts.budgetMs}ms spent, stopping the ladder at ${candidates.length} rungs`);
      break;
    }
    const want = new Set(set);
    const lines: PlanarLine[] = [];
    for (const b of barriers) if (want.has(b.kind)) lines.push(b);
    const c0 = Date.now();
    const out = enclosingBlock(lines, center, { radii });
    const ms = Date.now() - c0;
    if (!out.ok) {
      lastReason = out.reason;
      candidates.push({
        barriers: set,
        block: null,
        reason: out.reason,
        areaSqM: 0,
        ratio: 0,
        withinBound: false,
        edgeSafety: 0,
        edgeMix: {},
        score: -Infinity,
        ms,
      });
      opts.trace?.(`[${set.join('+')}] ${out.reason} (${ms}ms)`);
      continue;
    }
    const area = out.block.areaSqMeters;
    const ratio = area / target;
    const safety = edgeSafety(out.block.ring, barriers.filter((b) => want.has(b.kind)), center);
    candidates.push({
      barriers: set,
      block: out.block,
      areaSqM: area,
      ratio,
      withinBound: ratio <= model.overshoot,
      edgeSafety: safety.score,
      edgeMix: safety.mix,
      score: 0,
      ms,
    });
    opts.trace?.(
      `[${set.join('+')}] ${(area / 2_589_988.11).toFixed(3)} sq mi, ${ratio.toFixed(2)}x target, ` +
        `edge ${safety.score.toFixed(2)} (${ms}ms)`,
    );
  }

  const found = candidates.filter((c) => c.block);
  if (!found.length) return { ok: false, reason: lastReason, targetSqM: target, candidates, ms: Date.now() - t0 };

  /**
   * SIZE FIT, 0..1. Symmetric in log space so "half the target" and "twice the
   * target" are penalised alike; a plain |area - target| would call a 0.01 sq mi
   * answer nearly perfect just because the absolute miss is small.
   */
  const fit = (c: Candidate) => 1 / (1 + Math.abs(Math.log(Math.max(c.ratio, 1e-6))));
  for (const c of found) c.score = (1 - edgeWeight) * fit(c) + edgeWeight * c.edgeSafety;

  // Rule 2. Prefer overshoot: among the rungs at or under the bound, take the
  // BIGGEST, then let edge safety separate anything within 10% of it.
  //
  // The tiebreak is EDGE SAFETY ALONE, deliberately, and that is what makes
  // criterion 6's "areas non-decreasing foot -> bike -> car" a theorem instead
  // of an observation. A coarser mode's ladder contains every rung a finer
  // mode's does, and its bound is looser, so its in-bound set is a superset;
  // a rung's area and its edge safety are both properties of the BARRIER SET,
  // not of the mode. Rank the near-set by anything mode-dependent - size fit
  // against a mode-specific target, say - and car can prefer a smaller rung
  // than bike did out of the identical shortlist.
  const inBound = found.filter((c) => c.withinBound);
  let chosen: Candidate;
  let sizeCompromised = false;
  if (inBound.length) {
    const biggest = Math.max(...inBound.map((c) => c.areaSqM));
    const near = inBound.filter((c) => c.areaSqM >= biggest * 0.9);
    chosen = near.reduce((a, b) => (b.edgeSafety > a.edgeSafety ? b : a));
  } else {
    // Every rung overshoots. Take the SMALLEST real block rather than nothing:
    // a map that is too big is a game, and a refusal is not. This is the path
    // that stops car reporting "no area" where a genuine block exists.
    chosen = found.reduce((a, b) => (b.areaSqM < a.areaSqM ? b : a));
    sizeCompromised = true;
  }
  return { ok: true, chosen, targetSqM: target, candidates, ms: Date.now() - t0, sizeCompromised };
}
